import { useEffect, useRef, useState } from "react";
import "./App.css";
import {
  brand,
  navLinks,
  images,
  heroEyebrow,
  heroLede,
  aboutText,
  expertise,
  whyCards,
  contact,
  developer,
} from "./mock";

/* -------- Why Choose Us icons (matching the original line-art SVGs) -------- */
const WhyIcon = ({ name }) => {
  const icons = {
    pricing: (
      <svg viewBox="0 0 48 48">
        <circle cx="24" cy="24" r="16" />
        <path d="M24 15v18M29 19.5c0-2.5-2.5-4.5-5.5-4.5S18 16.8 18 19.5c0 5 11 3.6 11 8.6 0 2.7-2.7 4.9-6 4.9s-6-1.8-6-4.4" />
      </svg>
    ),
    team: (
      <svg viewBox="0 0 48 48">
        <circle cx="24" cy="16" r="7" />
        <path d="M10 40c0-8 6-13 14-13s14 5 14 13" />
      </svg>
    ),
    quality: (
      <svg viewBox="0 0 48 48">
        <path d="M24 6l15 6v11c0 10-6.5 17-15 19-8.5-2-15-9-15-19V12z" />
        <path d="M17 24l5 5 10-11" />
      </svg>
    ),
    customer: (
      <svg viewBox="0 0 48 48">
        <path
          d="M24 42s-15-9.5-15-20.5C9 14 14 9 20 9c2.6 0 5 1.2 6.5 3.2C28 10.2 30.4 9 33 9c6 0 11 5 11 12.5C44 32.5 24 42 24 42z"
          transform="scale(0.9) translate(2,2)"
        />
      </svg>
    ),
  };
  return <div className="why-icon">{icons[name]}</div>;
};

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeId, setActiveId] = useState("home");
  const [btnText, setBtnText] = useState("Send Inquiry");
  const revealRefs = useRef([]);

  /* header scroll state */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* scroll reveal */
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );
    revealRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  /* active nav link on scroll */
  useEffect(() => {
    const sections = document.querySelectorAll("section[id]");
    const spy = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );
    sections.forEach((s) => spy.observe(s));
    return () => spy.disconnect();
  }, []);

  const goTo = (e, id) => {
    e.preventDefault();
    setMenuOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    setBtnText("Message Sent");
    e.target.reset();
    setTimeout(() => setBtnText("Send Inquiry"), 2600);
  };

  const addReveal = (el) => {
    if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el);
  };

  return (
    <div className="pisces">
      {/* ---------- HEADER ---------- */}
      <header className={scrolled ? "scrolled" : ""} id="siteHeader">
        <a href="#home" className="brand" onClick={(e) => goTo(e, "home")}>
          <img
            src="/pisces-logo.png"
            alt="Pisces Group LLC — Sourcing Excellence. Delivering Elegance."
            className="brand-logo"
          />
        </a>
        <nav className={`main-nav ${menuOpen ? "open" : ""}`} id="mainNav">
          {navLinks.map((l) => (
            <a
              key={l.id}
              href={`#${l.id}`}
              className={`nav-link ${activeId === l.id ? "active" : ""}`}
              onClick={(e) => goTo(e, l.id)}
            >
              {l.label}
            </a>
          ))}
        </nav>
        <button
          className={`menu-toggle ${menuOpen ? "open" : ""}`}
          aria-label="Toggle menu"
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </header>

      {/* ---------- HOME / HERO ---------- */}
      <section id="home" className="hero">
        <div
          className="hero-bg"
          style={{ backgroundImage: `url('${images.hero}')` }}
        ></div>
        <div className="hero-content">
          <div className="hero-eyebrow">
            {heroEyebrow.map((t, i) => (
              <span key={i}>
                {t}
                {i < heroEyebrow.length - 1 && <>&nbsp;&middot;&nbsp;</>}
              </span>
            ))}
          </div>
          <p className="lede">{heroLede}</p>
          <a href="#contact" className="btn" onClick={(e) => goTo(e, "contact")}>
            Start a Project
          </a>
        </div>
        <div className="scroll-cue"></div>
      </section>

      <img src={images.dividerFacade} className="divider-img" alt="" />

      {/* ---------- ABOUT ---------- */}
      <section id="about" className="reveal" ref={addReveal}>
        <div className="container about-grid">
          <div className="about-copy">
            <div className="eyebrow">Who We Are</div>
            <h2 className="section-title">About us&hellip;</h2>
            <p>{aboutText}</p>
          </div>
          <div className="about-photos">
            <img
              src={images.aboutConstruction}
              alt="Construction and building project managed by Pisces Group LLC"
              className="p1"
            />
            <img
              src={images.aboutKitchen}
              alt="Luxury kitchen furnishings sourced by Pisces Group LLC"
              className="p2"
            />
          </div>
        </div>
      </section>

      {/* ---------- EXPERTISE ---------- */}
      <section id="expertise" className="reveal" ref={addReveal}>
        <div className="container">
          <div className="eyebrow">What We Do</div>
          <h2 className="section-title">Our Expertise&hellip;</h2>
          <p className="section-sub">
            From foundation to finishing details, we ensure durability,
            efficiency, and sophistication in every product.
          </p>

          <div className="expertise-list">
            {expertise.map((x) => (
              <div className="expertise-row" key={x.num}>
                <div className="text">
                  <span className="num">{x.num}</span>
                  <h3>{x.title}</h3>
                  <p>{x.text}</p>
                </div>
                <div className="photo">
                  <img src={x.img} alt={x.alt} loading="lazy" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- WHY CHOOSE US ---------- */}
      <section id="why" className="reveal" ref={addReveal}>
        <div className="container">
          <div className="eyebrow">Our Promise</div>
          <h2 className="section-title">Why Choose Us</h2>
          <p className="section-sub">
            We're committed to delivering exceptional service on every project.
            Here's what sets us apart from the competition.
          </p>

          <div className="why-grid">
            {whyCards.map((c) => (
              <div className="why-card" key={c.title}>
                <WhyIcon name={c.icon} />
                <h3>{c.title}</h3>
                <p>{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <img src={images.dividerBlueprint} className="divider-img" alt="" />

      {/* ---------- CONTACT ---------- */}
      <section id="contact" className="reveal" ref={addReveal}>
        <div className="container contact-grid">
          <div>
            <div className="eyebrow">Get In Touch</div>
            <h2 className="section-title">Contact Us</h2>
            <p className="section-sub">{contact.intro}</p>

            <div className="contact-block">
              <div className="contact-label">Address</div>
              <div className="contact-value">
                {contact.address.map((line, i) => (
                  <span key={i}>
                    {line}
                    {i < contact.address.length - 1 && <br />}
                  </span>
                ))}
              </div>
            </div>
            <div className="contact-block">
              <div className="contact-label">Phone</div>
              <div className="contact-value">
                {contact.phones.map((p, i) => (
                  <span key={p.href}>
                    <a href={p.href}>{p.label}</a>
                    {i < contact.phones.length - 1 && <>&nbsp;&middot;&nbsp;</>}
                  </span>
                ))}
              </div>
            </div>
            <div className="contact-block">
              <div className="contact-label">Email</div>
              <div className="contact-value">
                <a href={`mailto:${contact.email}`}>{contact.email}</a>
              </div>
            </div>

            <div className="social-row">
              <a
                href={contact.facebook}
                aria-label="Facebook"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg viewBox="0 0 24 24">
                  <path d="M13.5 21v-8.1h2.7l.4-3.2h-3.1V7.7c0-.9.3-1.6 1.6-1.6h1.7V3.2C16.5 3.1 15.5 3 14.3 3c-2.5 0-4.2 1.5-4.2 4.3v2.4H7.4v3.2h2.7V21h3.4z" />
                </svg>
              </a>
            </div>
          </div>

          <form className="contact-form" id="contactForm" onSubmit={onSubmit}>
            <div className="field">
              <label htmlFor="name">Full Name</label>
              <input type="text" id="name" name="name" required />
            </div>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input type="email" id="email" name="email" required />
            </div>
            <div className="field">
              <label htmlFor="message">Tell us about your project</label>
              <textarea id="message" name="message" required></textarea>
            </div>
            <button type="submit" className="btn">
              {btnText}
            </button>
          </form>
        </div>
      </section>

      {/* ---------- FOOTER ---------- */}
      <footer>
        <div className="container footer-inner">
          <p>
            &copy; {brand.year} THE PISCES GROUP LLC. All rights reserved.
          </p>
          <p>
            Designed &amp; Developed by{" "}
            <a href={developer.href} target="_blank" rel="noopener noreferrer">
              {developer.label}
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
